/**
 * Table Booking real-time updates (Customer Portal) – Ably.
 * Subscribes to user:${userId} for table booking, booking message, and seat assignment notifications.
 * Optionally subscribes to branch:${branchId} for table booking-opened and seat events.
 * See docs/ABLY_CUSTOMER_PORTAL.md, docs/BOOKING_OPENED_AND_CHAIR_SEATS_API.md.
 */
import { getAblyRealtime, subscribeToChannel, isAblyConnected } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const USER_EVENTS = [
  'table_booking:approved',
  'table_booking:rejected',
  'table_booking:cancelled',
  'table_booking:checked_in',
  'table_booking:completed',
  'table_booking:is_table_yours_updated',
  'table_booking:is_booked_by_you_updated',
  // Cross-portal lifecycle events emitted by organizer actions.
  'table_booking:no_show',
  'table_booking:rescheduled',
  'table_booking:deposit',
  'table_booking:preorder',
  'table_booking:reschedule_declined',
  'table_booking:expired',
  'booking_waitlist:notified',
  'booking_message:notification',
  'table:booking_opened_updated',
  'booking_seat:assigned',
  'booking_seat:removed',
] as const;

const BRANCH_EVENTS = [
  'table:booking_opened_updated',
  'booking_seat:requested',
  'booking_seat:assigned',
  'booking_seat:removed',
  // Staff cleared/checked-out a table — free it in every viewer's grid live (no refresh).
  'table_booking:checked_out',
  // Staff changed a table's status (e.g. reserved a table for a phone-in customer) — reflect live.
  'table:status_updated',
] as const;

export class TableBookingWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubscribe: (() => void) | null = null;
  private unsubscribeBranch: (() => void) | null = null;
  private userId: string | null = null;
  private branchId: string | null = null;

  connect(): void {
    this.disconnectUser();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[TableBookingWebSocket] No access token available');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[TableBookingWebSocket] Could not extract userId from token');
      return;
    }

    this.userId = uid;
    const channelName = `user:${uid}`;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error(`[TableBookingWebSocket] handler ${event}:`, e);
        }
      });
    };

    this.unsubscribe = subscribeToChannel(
      channelName,
      USER_EVENTS as unknown as readonly string[],
      emit
    );

    // `on` (not `once`) so a RE-connect after a drop also fires 'connect' — consumers use it to
    // silently re-sync state missed while offline (past the 15s Ably rewind window).
    getAblyRealtime().connection.on('connected', () => {
      console.log('[TableBookingWebSocket] Subscribed to', channelName);
      emit('connect', undefined);
    });
    getAblyRealtime().connection.on('failed', (state: any) => {
      emit('error', state?.reason || new Error('Ably connection failed'));
    });
    getAblyRealtime().connection.on('disconnected', (state: any) => {
      emit('disconnect', state?.reason || 'disconnected');
    });
  }

  connectBranch(branchId: string): void {
    if (this.branchId === branchId && this.unsubscribeBranch) return;
    this.disconnectBranch();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) return;

    this.branchId = branchId;
    const channelName = `branch:${branchId}`;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error(`[TableBookingWebSocket] branch handler ${event}:`, e);
        }
      });
    };

    this.unsubscribeBranch = subscribeToChannel(
      channelName,
      BRANCH_EVENTS as unknown as readonly string[],
      emit
    );
    console.log('[TableBookingWebSocket] Subscribed to branch channel', channelName);
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    // Require the REAL Ably connection to be live — not merely that we hold a subscription — so a
    // poll fallback gated on this actually runs during a disconnected/suspended gap.
    return this.unsubscribe != null && this.userId != null && isAblyConnected();
  }

  private disconnectUser(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.userId = null;
  }

  private disconnectBranch(): void {
    if (this.unsubscribeBranch) {
      this.unsubscribeBranch();
      this.unsubscribeBranch = null;
    }
    this.branchId = null;
  }

  disconnect(): void {
    this.disconnectUser();
    this.disconnectBranch();
    this.eventHandlers.clear();
    console.log('[TableBookingWebSocket] Disconnected');
  }
}

export const tableBookingWebSocketService = new TableBookingWebSocketService();
